import { Grid } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import PageHeader from '../shared/layout/PageHeader/PageHeader'
import Birthday from './Birthday'
import { RootStore } from '../redux/store'
import AllowanceChart from './AllowanceChart'
import AllocationExtras from './AllocationExtras'
import { AllocationExtrasType } from '../types/allocation-extras-type'
import FullComponentLoadingIcon from '../shared/UI/LoadingIndicator/FullComponentLoadingIcon'
import { useEntitlement } from './Dashboard/useEntitlement'
import MobileContainer from '../shared/layout/MobileContainer'

function MyExtras() {
  const [serviceHours, setServiceHours] = useState<AllocationExtrasType>({
    service: 0,
    fireMarshall: 0,
    firstAid: 0,
  })
  const [serviceHoursLoading, setServiceHoursLoading] = useState<boolean>(true)
  const [myExtrasLoading, setMyExtrasLoading] = useState<boolean>(true)

  const {
    myEntitlement,
    isEntitlementLoading,
    buyHours,
    sellHours,
    requestedSellHours,
    refreshEntitlement,
    fetchSellData,
  } = useEntitlement()

  const { userSettings } = useSelector((state: RootStore) => state.appSettings)

  const hasBuy = useMemo(() => Boolean(userSettings?.isBuyRequestAllowed), [userSettings])
  const hasSell = useMemo(() => Boolean(userSettings?.isSellRequestAllowed), [userSettings])
  const hasBuyOrSell = useMemo(() => hasBuy || hasSell, [hasBuy, hasSell])
  const isBirthdayAllowed = useMemo(
    () => Boolean(userSettings?.isBirthdayRequestAllowed),
    [userSettings]
  )

  useEffect(() => {
    if (myEntitlement && !isEntitlementLoading) {
      setServiceHours(prevState => ({
        ...prevState,
        fireMarshall: myEntitlement.fireMarshalAllowance,
        firstAid: myEntitlement.firstAiderAllowance,
        service: myEntitlement.serviceHours,
      }))
      setServiceHoursLoading(false)
    }
  }, [isEntitlementLoading, myEntitlement])

  const hasServiceHours = useMemo(
    () => serviceHours.fireMarshall > 0 || serviceHours.firstAid > 0 || serviceHours.service > 0,
    [serviceHours]
  )

  const allThreeCardsDisplayed = useMemo(
    () => hasServiceHours && hasBuyOrSell && isBirthdayAllowed,
    [hasBuyOrSell, hasServiceHours, isBirthdayAllowed]
  )

  useEffect(() => {
    if (!serviceHoursLoading && userSettings !== null) {
      setMyExtrasLoading(false)
      return
    }

    setMyExtrasLoading(true)
  }, [serviceHoursLoading, userSettings])

  return (
    <>
      <PageHeader title="My Extras" />
      <MobileContainer>
        <FullComponentLoadingIcon
          loading={myExtrasLoading}
          bgColor="transparent"
          sx={{ height: 'auto' }}
        >
          <Grid container spacing={4}>
            {/* Col One */}
            {hasBuyOrSell && (
              <Grid item xs={12} md={6} xl={4} display="flex">
                <AllowanceChart
                  title="My Holidays"
                  buyHours={buyHours}
                  sellHours={sellHours}
                  myEntitlement={myEntitlement}
                  isLoadingEntitlement={isEntitlementLoading}
                  requestedSellHours={requestedSellHours}
                  refreshEntitlement={refreshEntitlement}
                  fetchSellData={fetchSellData}
                />
              </Grid>
            )}
            {/* Col One End */}
            {/* Col Two */}
            {hasServiceHours && (
              <Grid item xs={12} md={6} xl={4} display="flex">
                <AllocationExtras serviceHours={serviceHours} isLoading={serviceHoursLoading} />
              </Grid>
            )}
            {/* Col Two End */}
            {/* Col Three */}
            {isBirthdayAllowed && (
              <Grid item xs={12} md={allThreeCardsDisplayed ? 12 : 6} xl={4}>
                <Birthday />
              </Grid>
            )}
            {/* Col Three End */}
          </Grid>
        </FullComponentLoadingIcon>
      </MobileContainer>
    </>
  )
}

export default MyExtras
